import axios from 'axios';
import { useCallback, useRef, useState } from 'react';

import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { logger } from '@/services/logger';
import { restUrl } from '@/services/utils';

const countItems = async (itemsQuery: Record<string, unknown>): Promise<number> => {
    const response = await axios.post<{ numberOfItems?: number }>(restUrl('items/count'), { itemsQuery });
    return Number(response.data?.numberOfItems ?? 0);
};

/**
 * Exact total behind the refresh icon, `FetchItemController#countItems`.
 * The result belongs to one set of restrictions: changing the search drops
 * it, and an answer for an older search is ignored.
 */
const useExactItemCount = (itemsQuery: Record<string, unknown>) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const queryKey = JSON.stringify(itemsQuery);
    const [result, setResult] = useState<{ queryKey: string; count: number } | null>(null);
    const [countingKey, setCountingKey] = useState<string | null>(null);
    const latestKey = useRef(queryKey);
    latestKey.current = queryKey;

    const refresh = useCallback(() => {
        const requestKey = queryKey;
        setCountingKey(requestKey);
        countItems(JSON.parse(requestKey) as Record<string, unknown>).then((count) => {
            if (latestKey.current === requestKey) {
                setResult({ queryKey: requestKey, count });
            }
        }).catch((error) => {
            logger.error('Counting items failed', error);
            if (latestKey.current === requestKey) {
                toast.error(t('ERROR_OCCURRED'));
            }
        }).finally(() => {
            setCountingKey((current) => (current === requestKey ? null : current));
        });
    }, [queryKey, t, toast]);

    const exactCount = result?.queryKey === queryKey ? result.count : undefined;
    return {
        exactCount,
        counting: countingKey === queryKey,
        recalculated: exactCount != null,
        refresh,
    };
};

export { countItems, useExactItemCount };
